import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { FileAudio, ShoppingCart, UploadCloud } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { db } from "../firebase";
import { DEFAULT_PRICING, DEFAULT_SERVICES, formatCurrency, getServicePrice, mergePricing } from "../lib/platformData";
import { uploadServiceFiles } from "../lib/storageActions";
import { useToast } from "../context/ToastContext";
import { mergeSiteContent } from "../utils/helpers";
import { typographyFor } from "../lib/typography";

export default function ServicesPage() {
  const { user } = useAuth();
  const { addItem } = useCart();
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [selectedId, setSelectedId] = useState("mixage");
  const [files, setFiles] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (serviceId && DEFAULT_SERVICES.some((service) => service.id === serviceId)) {
      setSelectedId(serviceId);
    }
  }, [serviceId]);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "settings", "pricing"),
      (snap) => setPricing(mergePricing(snap.exists() ? snap.data() : DEFAULT_PRICING)),
      () => setPricing(DEFAULT_PRICING)
    );
    return () => off();
  }, []);

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      () => setSite(mergeSiteContent())
    );
    return () => off();
  }, []);

  const selectedService = useMemo(
    () => DEFAULT_SERVICES.find((service) => service.id === selectedId) || DEFAULT_SERVICES[0],
    [selectedId]
  );
  const selectedPrice = getServicePrice(selectedService, pricing);

  const addServiceToCart = async () => {
    setError("");
    setMessage("");

    if (!user) {
      navigate("/connexion", { state: { from: "/services" } });
      return;
    }

    setBusy(true);
    try {
      const uploadedFiles = files.length ? await uploadServiceFiles(user, selectedService.id, files) : uploaded;
      setUploaded(uploadedFiles);
      addItem({
        type: "service",
        serviceId: selectedService.id,
        name: selectedService.title,
        description: selectedService.description,
        unitPrice: selectedPrice,
        quantity: 1,
        currency: pricing.currency || "EUR",
        files: uploadedFiles,
      });
      toast.success("Service ajouté au panier", selectedService.title);
      navigate("/panier");
    } catch (err) {
      setError(err.message || "Impossible d'ajouter ce service.");
      toast.error("Service non ajouté", err.message || "Réessaie.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
      <section className="mx-auto max-w-7xl">
        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
          Services audio
        </p>
        <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <h1 className="text-5xl font-black uppercase leading-none md:text-7xl" style={typographyFor(site, "servicesTitle")}>
              Mixage, mastering, prod.
            </h1>
            <p className="readable-copy mt-4 max-w-2xl text-gray-400" style={typographyFor(site, "servicesText")}>
              Confie-nous ton morceau et ses fichiers. Nous lui donnerons la finition qu'il mérite.
            </p>
          </div>
          <Link to="/compte" className="inline-flex w-fit rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase">
            Mon espace
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
          <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {DEFAULT_SERVICES.map((service) => {
              const active = selectedId === service.id;
              const price = getServicePrice(service, pricing);
              return (
                <button
                  key={service.id}
                  onClick={() => {
                    setSelectedId(service.id);
                    navigate(`/services/${service.id}`, { replace: false });
                    setFiles([]);
                    setUploaded([]);
                    setMessage("");
                    setError("");
                  }}
                  className={`rounded-lg border p-5 text-left transition ${
                    active ? "border-red-500 bg-red-600/15" : "border-white/10 bg-zinc-950 hover:border-white/30"
                  }`}
                >
                  <div className="mb-8 grid h-12 w-12 place-items-center rounded-full bg-white text-black">
                    <FileAudio size={20} />
                  </div>
                  <h2 className="text-2xl font-black uppercase" style={typographyFor(site, "servicesTitle")}>{service.title}</h2>
                  <p className="readable-copy mt-3 min-h-[88px] text-sm text-gray-400" style={typographyFor(site, "servicesText")}>{service.description}</p>
                  <p className="mt-5 text-xl font-black">
                    {price > 0 ? formatCurrency(price, pricing.currency) : "Prix configurable"}
                  </p>
                </button>
              );
            })}
          </section>

          <aside className="rounded-lg border border-white/10 bg-zinc-950 p-5">
            <h2 className="text-2xl font-black uppercase" style={typographyFor(site, "servicesTitle")}>{selectedService.title}</h2>
            <p className="readable-copy mt-3 text-gray-400" style={typographyFor(site, "servicesText")}>{selectedService.description}</p>
            <div className="mt-5 rounded-lg border border-white/10 bg-black p-4">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Prix</span>
                <span>{selectedPrice > 0 ? formatCurrency(selectedPrice, pricing.currency) : "Admin"}</span>
              </div>
              <div className="mt-3 flex justify-between border-t border-white/10 pt-3 text-lg font-black">
                <span>Total</span>
                <span>{formatCurrency(selectedPrice, pricing.currency)}</span>
              </div>
            </div>

            <label className="mt-5 block cursor-pointer rounded-lg border border-dashed border-white/20 bg-black p-5 text-center transition hover:border-red-500">
              <UploadCloud className="mx-auto mb-3 text-red-500" size={28} />
              <span className="block text-sm font-bold uppercase">{selectedService.uploadLabel}</span>
              <span className="mt-2 block text-xs text-gray-500">Audio, zip ou references</span>
              <input
                type="file"
                multiple
                accept="audio/*,.zip,.rar,.wav,.mp3,.aiff,.flac"
                className="hidden"
                onChange={(event) => setFiles(Array.from(event.target.files || []))}
              />
            </label>

            {files.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                {files.map((file) => (
                  <li key={`${file.name}-${file.size}`} className="rounded border border-white/10 bg-black px-3 py-2">
                    {file.name}
                  </li>
                ))}
              </ul>
            )}

            {message && <p className="mt-4 text-sm text-green-200">{message}</p>}
            {error && <p className="mt-4 text-sm text-red-200">{error}</p>}

            {!user && (
              <p className="mt-4 rounded border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
                Connexion obligatoire pour commander et uploader.
              </p>
            )}

            <button
              onClick={addServiceToCart}
              disabled={busy}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white disabled:opacity-60"
            >
              <ShoppingCart size={18} />
              {busy ? "Upload..." : "Ajouter au panier"}
            </button>
          </aside>
        </div>
      </section>
    </main>
  );
}
